import { CommunityTile } from "@/components/missions/community-tile"
import { cn } from "@/lib/utils"
import type { CommunityPost } from "@/types/mission-community"

/** Tiles at/above which the 4th grid row is clipped for FOMO (UX Spec §2). */
export const CLIP_THRESHOLD = 10

/**
 * OKS-392 — The 3-column community grid (W11). Renders ≤12 `CommunityTile`s
 * row-major in backend order (most-recently-approved; Featured-first when view
 * data exists — V1 has none).
 *
 * FOMO clip: when ≥{CLIP_THRESHOLD} tiles render, the 4th row is clipped to
 * ~15% visible with a soft bottom fade so it reads as "there's more" — no
 * "load more" control.
 *
 * The clip height is pure CSS (no JS measurement → no layout thrash, stays
 * correct across breakpoints). The grid is a container-query context, so
 * `100cqw` = the grid's own width. With 3 square tiles and a `0.5rem` gap, one
 * row height = `(100cqw - 1rem) / 3`; 3 full rows + 3 inter-row gaps + ~15% of
 * the 4th row = `1.05 * (100cqw - 1rem) + 1.5rem`. The fade overlay softens the
 * cut so the partial row reads as "there's more".
 */
export interface CommunityFeedGridProps {
  posts: CommunityPost[]
}

const CLIP_MAX_HEIGHT = "calc(1.05 * (100cqw - 1rem) + 1.5rem)"

export function CommunityFeedGrid({ posts }: CommunityFeedGridProps) {
  const clip = posts.length >= CLIP_THRESHOLD

  return (
    <div
      data-testid="community-feed"
      data-clip={clip}
      style={{ containerType: "inline-size" }}
      className={cn("relative", clip && "overflow-hidden")}
    >
      <ul
        data-testid="community-feed-grid"
        className="grid grid-cols-3 gap-2"
        style={clip ? { maxHeight: CLIP_MAX_HEIGHT } : undefined}
      >
        {posts.map((post) => (
          <li key={post.id}>
            <CommunityTile post={post} />
          </li>
        ))}
      </ul>

      {/* Soft fade over the clipped 4th row. */}
      {clip && (
        <div
          aria-hidden="true"
          data-testid="community-feed-fade"
          className="from-background pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t to-transparent"
        />
      )}
    </div>
  )
}
